/**
 * Serviço Unificado de Verificação de Permissões
 * 
 * Este serviço é a fonte única de verdade para verificação de permissões.
 * Sempre consulta permissionConfig diretamente, nunca permissions.features.
 * 
 * Funções puras que podem ser testadas independentemente.
 */

import { UserRole } from '../../types/permissions';
import { PermissionResult, PermissionStatus, PERMISSION_MESSAGES } from './permissionTypes';
import { logger } from '../terminalLogger';
import { isToolFreeFromManifest, getFeatureKeyFromManifest } from './featuresManifest';

/**
 * Configuração de logging para validação
 * Pode ser desabilitada via variável de ambiente
 */
const ENABLE_VALIDATION_LOGS = import.meta.env.DEV || import.meta.env.VITE_ENABLE_PERMISSION_LOGS === 'true';

/**
 * Contadores de verificação (apenas em desenvolvimento)
 * Útil para identificar padrões de uso e problemas de performance
 */
const validationCounters: Record<string, number> = {};

/**
 * Cache de logs já emitidos para evitar repetição
 * Reseta automaticamente a cada 5 segundos
 */
const loggedWarningsCache = new Set<string>();
let lastCacheClear = Date.now();
const CACHE_TTL_MS = 5000; // 5 segundos

/**
 * Incrementa contador de verificação (apenas em desenvolvimento)
 */
function incrementCounter(key: string): void {
  if (!ENABLE_VALIDATION_LOGS) return;
  validationCounters[key] = (validationCounters[key] || 0) + 1;
}

/**
 * Loga warning com deduplicação (evita spam de logs repetidos)
 */
function logWarningOnce(key: string, message: string, data: Record<string, any>): void {
  if (!ENABLE_VALIDATION_LOGS) return;

  // Limpa cache se passou o TTL
  const now = Date.now();
  if (now - lastCacheClear > CACHE_TTL_MS) {
    loggedWarningsCache.clear();
    lastCacheClear = now;
  }

  // Só loga se não estiver no cache
  if (!loggedWarningsCache.has(key)) {
    logger.warn(message, data);
    loggedWarningsCache.add(key);
  }
}

/**
 * Obtém contadores de validação (apenas em desenvolvimento)
 */
export function getValidationCounters(): Record<string, number> {
  return { ...validationCounters };
}

/**
 * Reseta contadores de validação (apenas em desenvolvimento)
 */
export function resetValidationCounters(): void {
  if (!ENABLE_VALIDATION_LOGS) return;
  Object.keys(validationCounters).forEach(key => {
    delete validationCounters[key];
  });
}

/**
 * Interface para PermissionConfig (deve corresponder à interface em PermissionContext)
 * Mantida aqui para evitar dependência circular.
 *
 * Novo formato normalizado: features é um Record<feature_key, boolean>.
 * Exemplos de feature_key: 'tool.oneregua', 'general.can_save', 'calculator.can_export'
 */
export interface PermissionConfig {
  perm_id: number;
  role_name?: string | null;
  features: Record<string, boolean>;
}

/**
 * Lista de ferramentas que não precisam de verificação de permissão
 */
const TOOLS_NO_PERMISSION_REQUIRED: string[] = [
  'contracao-dtf-textil',
  'contracao-dtf-uv',
  'girar-objeto',
  'layout-grid', // Layout Grid - disponível para todas as roles
  'criar-retangulo', // Folha A3 UV - disponível para todas as roles
  'refresh', // Atualizar Tela - disponível para todas as roles
  'limpar-cache', // Limpar Cache - disponível para todas as roles
  'multiplicar-objeto-v2', // Multiplicar v2.0 - disponível para todas as roles
  'gerenciar-camadas',     // Gerenciar Camadas - disponível para todas as roles
  'close-document',        // Fechar Documento - disponível para todas as roles
  'auto-bitmap',           // Auto Bitmap - disponível para todas as roles
  'montatech-native',      // MontaTech 2.0 nativo - disponível para todas as roles
  'resize-v2',             // Resize v2 - temporário para teste
  'reverter-pipeline',     // Reverter Organização - disponível para todas as roles (desfaz organizar/distribuir)
];

/**
 * Mapeamento de toolId do código para feature_key no Feature Flag Registry.
 * Formato das chaves: 'categoria.nome' (ex: 'tool.oneregua', 'general.can_save')
 */
const TOOL_PERMISSION_MAP: Record<string, string> = {
  'oneregua':              'tool.oneregua',
  'onecalc':               'tool.onecalc',
  'onecontour':            'tool.onecontour',
  'onecontour-v2':         'tool.onecontour',
  'onecolorcheck':         'tool.onecolorcheck',
  'onebitmap':             'tool.onebitmap',
  'onebitmapv1':           'tool.onebitmap',
  'cmd_montatec':          'tool.montatec',
  'cmd_mult_montatec':     'tool.montatec',
  'smartimport':           'tool.smartimport',
  'resize':                'tool.smartimport',
  'resize-v2':             'tool.resize',
  'transparencia':         'tool.smartimport',
  'runps':                 'tool.runps',
  'oneagrupar':            'tool.oneagrupar',
  'montatech':             'tool.montatech',  'moldesone':             'tool.moldesone',
  'moldeone-v2':           'tool.moldesone',
  'export_batch_local':    'tool.export_batch_local',
  'export_producao_offline': 'tool.export_producao_offline',
  'repair-pdf':            'tool.repair_pdf',
  'testsmb':               'tool.test_network',
  'multiplicador-objetos': 'tool.multiplicador_objetos',
  'grid-objetos':          'tool.grid_objetos',
  'quotation-calculator':  'tool.quotation_calculator',
  // Admin only — sem feature_key no banco
  'collectmetadata':       '_admin_only',
};

/**
 * Valida estrutura básica do permissionConfig
 * Retorna true se estrutura é válida, false caso contrário
 */
function validatePermissionConfigStructure(config: PermissionConfig | null): boolean {
  if (!config) return false;

  if (typeof config.perm_id !== 'number') {
    if (ENABLE_VALIDATION_LOGS) {
      logger.warn('[PERMISSION] ⚠️ permissionConfig sem perm_id válido', {
        hasConfig: !!config,
        availableFields: config ? Object.keys(config) : []
      });
    }
    return false;
  }

  if (!config.features || typeof config.features !== 'object') {
    if (ENABLE_VALIDATION_LOGS) {
      logger.warn('[PERMISSION] ⚠️ permissionConfig sem features object', {
        perm_id: config.perm_id,
        hasFeatures: !!config.features
      });
    }
    return false;
  }

  return true;
}

/**
 * Helper para acessar uma feature_key dentro do objeto features do permissionConfig
 */
function getPermissionConfigValue(config: PermissionConfig | null, key: string): unknown {
  if (!config?.features || typeof config.features !== 'object') {
    return undefined;
  }
  const value = config.features[key];
  return value === undefined ? undefined : value;
}

/**
 * Verifica permissão de ferramenta específica
 * 
 * Lógica:
 * 1. Admin sempre tem acesso
 * 2. Ferramentas sem permissão sempre permitidas
 * 3. Ferramentas admin-only bloqueadas para não-admins
 * 4. Consulta permissionConfig diretamente
 * 5. Se flag não existe, BLOQUEIA (fail-safe)
 * 
 * @param toolId ID da ferramenta (ex: 'oneregua', 'cmd_montatec')
 * @param permissionConfig Configuração de permissões da API
 * @param isAdmin Se o usuário é admin
 * @returns PermissionResult com allowed, status e reason
 */
export function checkToolPermission(
  toolId: string,
  permissionConfig: PermissionConfig | null,
  isAdmin: boolean
): PermissionResult {
  incrementCounter(`tool:${toolId}`);

  // Admin sempre tem acesso
  if (isAdmin) {
    if (ENABLE_VALIDATION_LOGS) {
      logger.debug('[PERMISSION] ✅ Tool permitido (admin)', { toolId });
    }
    return { allowed: true, status: 'allowed' as const };
  }

  // Ferramentas que não precisam de permissão sempre permitidas
  // Prioridade 1: manifest carregado do banco (dinâmico)
  // Prioridade 2: constante hardcoded (fallback para quando manifest ainda não carregou)
  // Prioridade 1: manifest carregado do banco (dinâmico)
  // Prioridade 2: constante hardcoded (fallback para quando manifest ainda não carregou OU feature não cadastrada)
  const manifestFree = isToolFreeFromManifest(toolId);
  const isFree = manifestFree ?? TOOLS_NO_PERMISSION_REQUIRED.includes(toolId);
  if (isFree) {
    if (ENABLE_VALIDATION_LOGS) {
      logger.debug('[PERMISSION] ✅ Tool permitido (sem permissão requerida)', {
        toolId,
        source: manifestFree !== null ? 'manifest' : 'hardcoded'
      });
    }
    return { allowed: true, status: 'allowed' as const };
  }

  // Mapear toolId para chave do banco
  // Prioridade 1: manifest (dinâmico) — Prioridade 2: hardcoded TOOL_PERMISSION_MAP
  const permissionKey = getFeatureKeyFromManifest(toolId) ?? TOOL_PERMISSION_MAP[toolId] ?? toolId;

  // Ferramentas marcadas como _admin_only só admin acessa
  if (permissionKey === '_admin_only') {
    if (ENABLE_VALIDATION_LOGS) {
      logger.debug('[PERMISSION] ❌ Tool bloqueado (admin only)', { toolId, permissionKey });
    }
    return {
      allowed: false,
      status: 'admin_only' as const,
      reason: PERMISSION_MESSAGES.admin_only
    };
  }

  // Validar estrutura do config
  if (!validatePermissionConfigStructure(permissionConfig)) {
    logWarningOnce(`config:invalid`, '[PERMISSION] ⚠️ Tool bloqueado (config inválido)', {
      toolId,
      permissionKey,
      hasConfig: !!permissionConfig
    });
    return {
      allowed: false,
      status: 'not_configured' as const,
      reason: PERMISSION_MESSAGES.not_configured
    };
  }

  // Estado transitório: features vazio = permissões ainda carregando do /auth/me
  // Bloqueia silenciosamente sem logar warning (evita spam durante bootstrap)
  if (permissionConfig && typeof permissionConfig.features === 'object'
      && Object.keys(permissionConfig.features).length === 0) {
    return {
      allowed: false,
      status: 'not_configured' as const,
      reason: 'Carregando permissões...'
    };
  }

  // Verifica flag específica da ferramenta
  const flag = getPermissionConfigValue(permissionConfig, permissionKey);

  // Se a flag existe e é boolean, usa ela
  if (typeof flag === 'boolean') {
    const result = flag
      ? { allowed: true, status: 'allowed' as const }
      : { allowed: false, status: 'denied' as const, reason: PERMISSION_MESSAGES.denied };

    if (ENABLE_VALIDATION_LOGS) {
      logger.debug(`[PERMISSION] ${result.allowed ? '✅' : '❌'} Tool ${result.allowed ? 'permitido' : 'bloqueado'}`, {
        toolId,
        permissionKey,
        flagValue: flag,
        status: result.status
      });
    }

    return result;
  }

  // Flag não existe no config - BLOQUEIA (fail-safe)
  // Usa deduplicação para evitar spam de logs repetidos
  logWarningOnce(`tool:${toolId}`, `[PERMISSION] ⚠️ Tool bloqueado (flag não existe): ${toolId} → ${permissionKey}`, {
    toolId,
    permissionKey,
    flagType: typeof flag,
    availableFlags: permissionConfig?.features ? Object.keys(permissionConfig.features) : []
  });

  return {
    allowed: false,
    status: 'not_configured' as const,
    reason: PERMISSION_MESSAGES.not_configured
  };
}

/**
 * Verifica permissão de feature/funcionalidade
 * 
 * Lógica:
 * 1. Admin sempre tem acesso
 * 2. Consulta permissionConfig diretamente (única fonte de verdade)
 * 3. Se flag não existe, BLOQUEIA (fail-safe)
 * 
 * IMPORTANTE: NÃO faz fallback para permissions.features
 * 
 * @param featureKey Chave da feature (ex: 'can_save', 'can_access_tools')
 * @param permissionConfig Configuração de permissões da API
 * @param isAdmin Se o usuário é admin
 * @returns PermissionResult com allowed, status e reason
 */
export function checkFeaturePermission(
  featureKey: string,
  permissionConfig: PermissionConfig | null,
  isAdmin: boolean
): PermissionResult {
  incrementCounter(`feature:${featureKey}`);

  // Admin sempre tem acesso
  if (isAdmin) {
    if (ENABLE_VALIDATION_LOGS) {
      logger.debug('[PERMISSION] ✅ Feature permitida (admin)', { featureKey });
    }
    return { allowed: true, status: 'allowed' as const };
  }

  // Validar estrutura do config
  if (!validatePermissionConfigStructure(permissionConfig)) {
    if (ENABLE_VALIDATION_LOGS) {
      logger.warn('[PERMISSION] ⚠️ Feature bloqueada (config inválido)', {
        source: 'src/lib/permissions/unifiedPermissionService.ts',
        function: 'checkFeaturePermission',
        featureKey,
        hasConfig: !!permissionConfig
      });
    }
    return {
      allowed: false,
      status: 'not_configured' as const,
      reason: PERMISSION_MESSAGES.not_configured
    };
  }

  // Consulta permissionConfig diretamente (única fonte de verdade)
  const flag = getPermissionConfigValue(permissionConfig, featureKey);

  // Se a flag existe e é boolean, usa ela
  if (typeof flag === 'boolean') {
    const result = flag
      ? { allowed: true, status: 'allowed' as const }
      : { allowed: false, status: 'denied' as const, reason: PERMISSION_MESSAGES.denied };

    if (ENABLE_VALIDATION_LOGS) {
      logger.debug(`[PERMISSION] ${result.allowed ? '✅' : '❌'} Feature ${result.allowed ? 'permitida' : 'bloqueada'}`, {
        featureKey,
        flagValue: flag,
        status: result.status,
        perm_id: permissionConfig?.perm_id,
        role_name: permissionConfig?.role_name
      });
    }

    return result;
  }

  // Flag não existe no config - BLOQUEIA (fail-safe)
  // NÃO faz fallback para permissions.features - permissionConfig é a única fonte de verdade
  if (ENABLE_VALIDATION_LOGS) {
    logger.warn('[PERMISSION] ⚠️ Feature bloqueada (flag não existe)', {
      source: 'src/lib/permissions/unifiedPermissionService.ts',
      function: 'checkFeaturePermission',
      featureKey,
      flagType: typeof flag,
      flagValue: flag,
      perm_id: permissionConfig?.perm_id,
      role_name: permissionConfig?.role_name,
      availableFlags: permissionConfig?.features ? Object.keys(permissionConfig.features) : []
    });
  }

  return {
    allowed: false,
    status: 'not_configured' as const,
    reason: PERMISSION_MESSAGES.not_configured
  };
}

/**
 * Verifica acesso baseado em role
 * 
 * @param requiredRoles Roles que têm acesso
 * @param currentRole Role atual do usuário
 * @returns true se o role atual está na lista de roles permitidos
 */
export function checkRoleBasedAccess(
  requiredRoles: UserRole[],
  currentRole: UserRole
): boolean {
  return requiredRoles.includes(currentRole);
}

/**
 * Verifica múltiplas ferramentas de uma vez
 * 
 * @param toolIds Array de IDs de ferramentas
 * @param permissionConfig Configuração de permissões da API
 * @param isAdmin Se o usuário é admin
 * @returns Record com resultado de cada ferramenta
 */
export function checkToolPermissions(
  toolIds: string[],
  permissionConfig: PermissionConfig | null,
  isAdmin: boolean
): Record<string, PermissionResult> {
  const result: Record<string, PermissionResult> = {};

  for (const toolId of toolIds) {
    result[toolId] = checkToolPermission(toolId, permissionConfig, isAdmin);
  }

  return result;
}

/**
 * Verifica múltiplas features de uma vez
 * 
 * @param featureKeys Array de chaves de features
 * @param permissionConfig Configuração de permissões da API
 * @param isAdmin Se o usuário é admin
 * @returns Record com resultado de cada feature
 */
export function checkFeaturePermissions(
  featureKeys: string[],
  permissionConfig: PermissionConfig | null,
  isAdmin: boolean
): Record<string, PermissionResult> {
  const result: Record<string, PermissionResult> = {};

  for (const featureKey of featureKeys) {
    result[featureKey] = checkFeaturePermission(featureKey, permissionConfig, isAdmin);
  }

  return result;
}
